/**
 * Authentication context provider
 */

import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, AuthApiError } from './authApi';
import { AuthContext } from './context';
import { clearEmulationData, endExpiredEmulation } from '../contexts/EmulationContext';
import { clearDemoMode } from './demoSession';
import { getBrowserTimezone } from '../utils/timezone';
import { clearPersistedQueryCache } from '../offline/queryPersister';
import { SESSION_EXPIRED_EVENT, resetSessionExpiredGuard } from '../api/axiosInstance';
import { isAppLockEngaged } from '../applock/appLockState';
import {
  SecureTokenStoreError,
  clearRefreshToken,
  getRefreshToken,
  setRefreshToken,
} from './secureTokenStore';
import { AUTH_STORAGE_KEYS } from './storageKeys';
import {
  TokenRefreshError,
  isEmulationAccessToken,
  isTokenExpiringSoon,
  refreshAccessTokens,
  type TokenPair,
} from './tokenRefresh';
import { isMfaChallenge } from './types';
import type {
  AuthContextType,
  AuthSessionResponse,
  AuthState,
  LoginOutcome,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  User } from './types';

// Error codes the server uses to explicitly reject a token. Anything outside
// this set — TypeError from fetch, 5xx, HTML_RESPONSE from a WAF — is treated
// as a transient/network condition and MUST NOT trigger sign-out at bootstrap.
const AUTH_REJECTION_CODES: ReadonlySet<string> = new Set([
  'UNAUTHORIZED',
  'AUTHENTICATION_REQUIRED',
  'INVALID_TOKEN',
  'INVALID_REFRESH_TOKEN',
  'TOKEN_EXPIRED',
]);

/**
 * Did the SERVER explicitly reject this token, as opposed to the request simply
 * failing to complete? Only a rejection may end a cached session; everything
 * else keeps an offline cold start signed in.
 *
 * Two error shapes reach here, and they spell the same distinction differently:
 * `AuthApiError` (raw-fetch `authApi` calls) carries a rejection CODE, while
 * `TokenRefreshError` (the shared single-flight exchange) carries the already-
 * classified `rejected` flag. Mapping the flag here is what lets the bootstrap
 * use the shared exchange without inheriting its callers' fail-closed policy.
 */
function isAuthRejection(err: unknown): boolean {
  if (err instanceof TokenRefreshError) return err.rejected;
  return err instanceof AuthApiError && AUTH_REJECTION_CODES.has(err.code);
}

function readStoredUser(): User | null {
  const raw = localStorage.getItem(AUTH_STORAGE_KEYS.user);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<User>;
    // Optimistic hydrate relies on these to route the user (ProtectedRoute
    // checks tenant + onboarding). A future User-schema change could leave a
    // stale blob missing fields — fall back to the blocking /me path instead.
    if (typeof parsed?.id !== 'string' || typeof parsed?.tenant_id !== 'string') {
      return null;
    }
    // `is_emulating` gets the same treatment for a different reason: it decides
    // whether product analytics counts this session as customer usage, and a
    // blob written before the field existed hydrates it as `undefined`, which
    // reads as "not emulating". Refusing the blob costs one blocking /me on the
    // first load after the deploy and never lies in the meantime.
    if (typeof parsed?.is_emulating !== 'boolean') {
      return null;
    }
    return parsed as User;
  } catch {
    return null;
  }
}

type TokenPreflight = {
  /** The token to send to `/auth/me` — the stored one, or its replacement. */
  accessToken: string;
  refreshToken: string | null;
  /** The stored pair WAS exchanged, so state must adopt the new one. */
  rotated: boolean;
  /** The server explicitly rejected the refresh token — don't ask twice. */
  refreshRejected: boolean;
};

/**
 * Exchange a spent access token BEFORE the bootstrap calls `/auth/me`.
 *
 * Access tokens live 15 minutes (`ACCESS_TOKEN_TTL`), so any hard navigation
 * after a short idle used to carry a known-dead token into `/me`: a guaranteed
 * red `401 (Unauthorized)` in the console on every cold load, followed by a
 * refresh and a second `/me`. The axios request interceptor has always made this
 * check, but `authApi.getMe` talks raw `fetch` and never reaches an interceptor.
 * Sharing `refreshAccessTokens` also means the bootstrap and any query firing
 * beside it join ONE `/auth/refresh` instead of racing two.
 *
 * Failing here is never fatal: we hand back the stored token and let the
 * existing `/me` → refresh → sign-out ladder decide, so an offline cold start
 * still keeps its cached session.
 */
async function preflightTokens(
  accessToken: string,
  refreshToken: string | null,
): Promise<TokenPreflight> {
  const unchanged: TokenPreflight = {
    accessToken,
    refreshToken,
    rotated: false,
    refreshRejected: false,
  };

  // A sysadmin emulation swaps only the ACCESS token; the stored refresh token
  // still belongs to the sysadmin, so exchanging it would mint a fresh sysadmin
  // access token and silently drop the operator out of emulation (the avatar
  // flips back, the emulated tenant's data vanishes — see refreshAuth below).
  // An emulation token is never refreshed; it just expires.
  if (!refreshToken || isEmulationAccessToken(accessToken)) return unchanged;
  if (!isTokenExpiringSoon(accessToken)) return unchanged;

  try {
    const tokens = await refreshAccessTokens();
    return {
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      rotated: true,
      refreshRejected: false,
    };
  } catch (error) {
    return {
      ...unchanged,
      refreshRejected: error instanceof TokenRefreshError && error.rejected,
    };
  }
}

/**
 * Forget the whole stored session — every part of it.
 *
 * All THREE keys, not just the refresh token. The bootstrap's fast path
 * hydrates a fully authenticated session from the access token plus the cached
 * user alone, so clearing only the refresh token leaves the device inside the
 * tenant until the access token expires (up to 15 minutes). That is the
 * difference between "signed out" and "signed out in a quarter of an hour".
 *
 * `clearRefreshToken()` raises its cleared latch synchronously, so a caller
 * that cannot await this — a click handler about to reload — is still safe.
 */
function clearStoredAuth(): Promise<void> {
  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.user);
  return clearRefreshToken();
}

/**
 * Is `accessToken` still the session on this device?
 *
 * Every bootstrap leg below decides across awaits — a keystore read of up to
 * KEYSTORE_READ_TIMEOUT_MS, a /me round trip, a refresh exchange — and a
 * DIFFERENT session can replace the stored one in any of those gaps: a login, a
 * logout, an emulation starting or ending, or any of those in ANOTHER TAB,
 * since localStorage is shared across the whole browser profile.
 *
 * Those legs end in `clearStoredAuth()` + `purgeQueryCache()`, and both act on
 * whatever is in storage NOW rather than on what the leg was reasoning about.
 * So a leg that resumes without asking this question signs the INCOMING session
 * out: the person logs in successfully and is thrown straight back to /login,
 * or loses their offline cache, on behalf of the session they just replaced.
 * The symptom is indistinguishable from a server-side rejection, so it is never
 * reported accurately either.
 *
 * The React-state guards (`prev.accessToken !== accessToken`) cover only the
 * half that has not happened yet — this is the half that has. Ask it BEFORE the
 * side effects, never inside the `setState` after them.
 *
 * Compared against the token the leg currently OWNS, which its own rotations
 * move forward (the shared exchange persists each rotated pair as it lands) —
 * not against the one it started with.
 */
function storedSessionIs(accessToken: string): boolean {
  return localStorage.getItem(AUTH_STORAGE_KEYS.accessToken) === accessToken;
}

/**
 * How long the bootstrap waits for the OS keystore before booting without the
 * refresh token.
 *
 * On native the refresh token lives in the Keychain / Android Keystore
 * (`secureTokenStore`), reached over the Capacitor bridge — an await with no
 * deadline of its own. Everything else the bootstrap touches has one
 * (`fetchWithDeadline`, axios's `timeout`, the query persister's
 * `RESTORE_TIMEOUT_MS`), and for the same reason: a leg of app startup that can
 * never answer parks `isLoading` at `true`, which renders as
 * `ProtectedRoute`'s spinner forever — no error, no console line, nothing the
 * person can do but reinstall.
 *
 * Sized like the persister's IndexedDB deadline rather than a network one: this
 * is local storage hardware, not a round trip, so a read that has not answered
 * in seconds is wedged rather than slow.
 */
const KEYSTORE_READ_TIMEOUT_MS = 5000;

/**
 * What the bootstrap learned about the stored refresh token.
 *
 * `answered: false` is deliberately NOT the same as a `null` token, for the
 * same reason `SecureTokenStoreError` is deliberately not "there is no token"
 * (see its class docstring). "There is no refresh token" is a fact about the
 * SESSION and ends it; "the keystore did not answer" is a fact about the
 * DEVICE and must not, or a cold Keychain on a slow handset signs someone out
 * of a session the server would gladly have refreshed — exactly the transient
 * condition this file's bootstrap doctrine exists to survive.
 */
type BootstrapRefreshToken = { answered: true; token: string | null } | { answered: false };

/**
 * Read the stored refresh token under a deadline, reporting WHICH of the two
 * outcomes above happened.
 *
 * A keystore that throws is reported as unanswered too, not as an absent
 * token: a device fault is a device fault whether it arrives as a rejection or
 * as silence.
 */
async function readRefreshTokenForBootstrap(): Promise<BootstrapRefreshToken> {
  // The timer is cleared on the winning path, like the query persister's own
  // `withTimeout`: a race leaves the loser running, and a five-second handle
  // left armed on every boot keeps a jsdom worker alive past the test that
  // created it.
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<BootstrapRefreshToken>((resolve) => {
    timer = setTimeout(() => resolve({ answered: false }), KEYSTORE_READ_TIMEOUT_MS);
  });
  try {
    return await Promise.race([
      getRefreshToken().then(
        (token): BootstrapRefreshToken => ({ answered: true, token }),
        (): BootstrapRefreshToken => ({ answered: false }),
      ),
      deadline,
    ]);
  } finally {
    clearTimeout(timer!);
  }
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);
  const queryClient = useQueryClient();

  // Purge the React Query cache (in-memory + persisted IndexedDB) on every auth
  // transition. The persisted cache is shared across every user on a browser
  // profile (one PersistQueryClientProvider at the app root) and internal
  // login/logout navigate via SPA navigate() with no full reload — so without
  // this, on a shared/kiosk browser user B would briefly render user A's cached
  // tasks/deals/financial data from cache. `clear()` empties memory
  // synchronously (before the next render); `removeClient()` deletes the
  // IndexedDB copy so a subsequent reload can't restore the stale snapshot.
  // Mirrors the external portal fix (ExternalAuthContext). Returns the promise
  // so identity-swap paths (login) can await the IndexedDB delete before
  // establishing the new session — closing the cross-tab restore race — while
  // full-teardown paths (expiry, bootstrap-reject) fire-and-forget it.
  const purgeQueryCache = useCallback(
    () => clearPersistedQueryCache(queryClient),
    [queryClient],
  );

  // Bootstrap runs exactly once per provider instance. React 18 StrictMode
  // mounts → unmounts → remounts every effect in development, which fired the
  // whole `/auth/me` revalidation twice on every page load (C-05). The token and
  // cached user this reads come from localStorage, not from React state, so a
  // remount has nothing new to observe — re-running it is pure duplicate I/O.
  const bootstrapStarted = useRef(false);

  // Load stored auth state on mount. Bootstrap is offline-safe: a network error
  // on /me or /refresh must NOT wipe a cached session — only an explicit auth
  // rejection from the server does. The axios 401 interceptor
  // (SESSION_EXPIRED_EVENT) remains the authoritative sign-out path for
  // requests made later from the running app.
  useEffect(() => {
    // Optimistically hydrate from cache, then revalidate /me in the background.
    // Only an explicit auth rejection (from /me AND, if present, /refresh) clears
    // the cached session; everything else keeps the user signed in.
    const revalidateInBackground = async (
      accessToken: string,
      stored: BootstrapRefreshToken,
    ) => {
      const refreshToken = stored.answered ? stored.token : null;

      // The token this revalidation owns, moved forward by its own rotations.
      // Anything else in storage means another session owns the device now —
      // see `storedSessionIs`.
      let sessionToken = accessToken;
      const sessionReplaced = () => !storedSessionIs(sessionToken);

      // Real sign-out. The stale token was optimistically hydrated on boot; the
      // persisted cache belongs to this now-rejected user, so purge it before we
      // clear the session.
      const signOut = () => {
        // Asked HERE, ahead of both side effects. The `setState` below asks the
        // same question, but by then `clearStoredAuth()` has already taken the
        // replacement session's tokens and `purgeQueryCache()` has already
        // dropped the incoming user's cached data.
        if (sessionReplaced()) return;
        void clearStoredAuth();
        void purgeQueryCache();
        setState((prev) => {
          if (prev.accessToken !== accessToken) return prev;
          return { ...initialState, isLoading: false };
        });
      };

      // The keystore read above can take seconds, and the pre-flight below
      // reads storage FRESH to exchange. Continuing past a replacement would
      // spend the NEW session's refresh token and then judge the answer as if
      // it were this one's.
      if (sessionReplaced()) return;

      // Refresh first when the stored token is already spent, so /me is never
      // asked a question we know the answer to (see preflightTokens).
      const preflight = await preflightTokens(accessToken, refreshToken);
      // A rotation this leg performed itself IS this leg's session.
      if (preflight.rotated) sessionToken = preflight.accessToken;

      // The pre-flight only runs when the access token is ALREADY spent, so a
      // refresh token the server rejected leaves nothing that could authenticate:
      // calling /me here could only 401. Sign out without the doomed round trip.
      if (preflight.refreshRejected) {
        signOut();
        return;
      }

      try {
        const currentUser = await authApi.getMe(preflight.accessToken);
        // The cached-user write is a storage side effect like any other: over a
        // replaced session it would leave the NEW token beside the OLD user, and
        // the next boot hydrates that pair optimistically.
        if (sessionReplaced()) return;
        localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(currentUser));
        setState((prev) => {
          // Skip if the session has been replaced (login, logout) since hydrate.
          if (prev.accessToken !== accessToken) return prev;
          if (!preflight.rotated) return { ...prev, user: currentUser };
          return {
            ...prev,
            user: currentUser,
            accessToken: preflight.accessToken,
            refreshToken: preflight.refreshToken,
          };
        });
        return;
      } catch (meError) {
        if (!isAuthRejection(meError)) return;
      }

      // /me rejected the token this leg is holding, and everything below ENDS a
      // session — tearing down an emulation, spending the refresh token, the
      // sign-out itself. Ask once here whose session is actually on the device;
      // nothing between this line and the exchange below awaits.
      //
      // This also declines to end an emulation whose token really is dead, in
      // the narrow case where the device changed hands first. That is the right
      // trade and it costs nothing: `endExpiredEmulation()` is idempotent and
      // self-guarding, and both axios interceptors and EmulationProvider's own
      // interval reach it independently — whereas tearing down an emulation the
      // replacement session never started would reload this tab out of a
      // context it does own.
      if (sessionReplaced()) return;

      // A rejected EMULATION access token is dead, and the stored refresh token
      // belongs to the sysadmin — exchanging it would resume their session
      // silently, behind a still-showing emulation banner (WI 59902ad6). End the
      // emulation explicitly instead; the provider reloads into sysadmin context.
      if (isEmulationAccessToken(accessToken)) {
        endExpiredEmulation();
        return;
      }

      // /me explicitly rejected — try refresh, unless the pre-flight already
      // rotated the pair, in which case this token IS fresh and the session is
      // genuinely over.
      if (refreshToken && !preflight.rotated) {
        // Set as soon as the exchange succeeds, so a LATER transient failure can
        // still adopt it. The shared exchange persists the rotated pair the
        // moment it lands, so if we returned without adopting it here, React
        // state would keep serving the spent pair to everything that reads the
        // token from context rather than storage — the notifications WebSocket
        // handshake, most of all — for the rest of the session.
        let rotated: TokenPair | null = null;
        try {
          // Join the shared single-flight instead of opening a second raw
          // exchange. An axios request interceptor may be refreshing right now
          // beside this bootstrap; two exchanges would present the SAME rotating
          // refresh token twice. Today the server blocklists only the OLD jti so
          // both succeed and one rotated pair is silently orphaned — the day
          // /auth/refresh starts rejecting a blocklisted INCOMING token, that
          // race becomes a spurious logout nobody can trace back to here.
          // `refreshAccessTokens` persists the rotated pair itself.
          const tokens = await refreshAccessTokens();
          rotated = tokens;
          sessionToken = tokens.access_token;
          const currentUser = await authApi.getMe(tokens.access_token);
          if (sessionReplaced()) return;
          localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(currentUser));
          setState((prev) => {
            if (prev.accessToken !== accessToken) return prev;
            return {
              ...prev,
              user: currentUser,
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
            };
          });
          return;
        } catch (refreshError) {
          // Network/transient on refresh must NOT wipe the session.
          if (!isAuthRejection(refreshError)) {
            // Adopt a rotation that DID happen before the failure, so state and
            // storage agree. Only on this branch: the sign-out below compares
            // `prev.accessToken` against the token this revalidation started
            // with, and moving state first would make it decline to clear.
            if (rotated) {
              const pair = rotated;
              setState((prev) => {
                if (prev.accessToken !== accessToken) return prev;
                return { ...prev, accessToken: pair.access_token, refreshToken: pair.refresh_token };
              });
            }
            return;
          }
        }
      }

      // Both /me and refresh explicitly rejected — but only if we ever got to
      // ASK the refresh token. With an unanswered keystore we skipped that
      // rung entirely, so /me's 401 does not prove the session is over; it
      // proves the 15-minute access token expired, which is routine. Keeping
      // the session is the same call this bootstrap makes for every other
      // transient condition, and the next launch re-asks.
      if (!stored.answered) return;
      signOut();
    };

    const loadStoredAuth = async () => {
      const storedAccessToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
      const storedUser = readStoredUser();

      // Decided from localStorage alone, ahead of the keystore read below: a
      // device with no session must reach the login page without waiting on a
      // native bridge round trip it has no use for.
      if (!storedAccessToken) {
        setState({ ...initialState, isLoading: false });
        return;
      }

      // Path A: token + cached user — hydrate immediately so the shell boots
      // offline; adopt the refresh token and revalidate in the background.
      //
      // The keystore read is deliberately NOT awaited before this `setState`.
      // On native it is a Capacitor bridge round trip, and it was the ONLY
      // unbounded await in the whole bootstrap — every other leg carries a
      // deadline (`fetchWithDeadline`, axios `timeout`, the persister's
      // `RESTORE_TIMEOUT_MS`). Sitting ahead of every path that clears
      // `isLoading`, a stalled read parked the app on `ProtectedRoute`'s
      // spinner with no error and no way out. Nothing in this hydrate needs
      // the refresh token: it is a fallback credential, not part of the shape
      // being rendered.
      if (storedUser) {
        setState({
          user: storedUser,
          accessToken: storedAccessToken,
          refreshToken: null,
          isAuthenticated: true,
          isLoading: false,
        });
        void (async () => {
          // Under the same deadline as Path B, and for a second reason: this
          // read gates the /me revalidation below, which is the ONLY thing
          // that notices a session revoked server-side while this device was
          // offline. An unbounded read here would not hang the UI — it would
          // silently skip that check for the life of the page load.
          const stored = await readRefreshTokenForBootstrap();
          setState((prev) => {
            // Same guard the revalidation uses: skip if login/logout replaced
            // the session while the keystore was answering.
            if (prev.accessToken !== storedAccessToken) return prev;
            return { ...prev, refreshToken: stored.answered ? stored.token : null };
          });
          await revalidateInBackground(storedAccessToken, stored);
        })();
        return;
      }

      // Path B: token but no cached user (e.g. emulation activation cleared it)
      // — we have nothing to render, so /me must succeed before we authenticate.
      //
      // This leg genuinely needs the refresh token before it can decide, so the
      // read stays on the critical path — but under a deadline, for the reason
      // above. A deadline that ELAPSED is carried as such rather than collapsed
      // into "no token": the ladder below ends in a sign-out, and a slow
      // keystore must not be the thing that triggers it.

      // This leg decides across the same awaits Path A does and ends in the same
      // storage clear, so it asks the same question (`storedSessionIs`) — and
      // localStorage is shared across tabs, so the replacement need not come
      // from this one.
      let sessionToken = storedAccessToken;
      const sessionReplaced = () => !storedSessionIs(sessionToken);

      // Hand the device back to whoever owns it now: touch neither storage nor
      // the cache, both of which belong to the replacement session.
      //
      // State still has to RESOLVE, though — unlike Path A this leg holds the
      // whole app on ProtectedRoute's spinner, and returning silently would park
      // it there. Only if nothing in this tab has decided already: a login here
      // sets `isLoading` false, and clobbering that would drop the person back
      // on /login holding a session they just created. A replacement from
      // another tab leaves this one unauthenticated with a valid session in
      // storage, which is precisely the disagreement `syncAuthOnVisible` reloads
      // on.
      const abandonToReplacementSession = () => {
        setState((prev) => (prev.isLoading ? { ...initialState, isLoading: false } : prev));
      };

      const stored = await readRefreshTokenForBootstrap();
      if (sessionReplaced()) return abandonToReplacementSession();

      const storedRefreshToken = stored.answered ? stored.token : null;
      const preflight = await preflightTokens(storedAccessToken, storedRefreshToken);
      // A rotation this leg performed itself IS this leg's session.
      if (preflight.rotated) sessionToken = preflight.accessToken;
      if (sessionReplaced()) return abandonToReplacementSession();

      // Access token already spent AND the refresh token rejected: nothing here
      // can authenticate, so /me could only 401. Sign out without asking.
      if (preflight.refreshRejected) {
        void clearStoredAuth();
        void purgeQueryCache();
        setState({ ...initialState, isLoading: false });
        return;
      }

      try {
        const currentUser = await authApi.getMe(preflight.accessToken);
        if (sessionReplaced()) return abandonToReplacementSession();
        localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(currentUser));
        setState({
          user: currentUser,
          accessToken: preflight.accessToken,
          refreshToken: preflight.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
        return;
      } catch (meError) {
        if (!isAuthRejection(meError)) {
          if (sessionReplaced()) return abandonToReplacementSession();
          // Network/transient: keep tokens for a later retry, but we have no
          // user shape to render against — leave isAuthenticated=false. The
          // resulting state is intentionally inconsistent (token present,
          // unauthenticated): a subsequent app foreground / reload will re-run
          // bootstrap once the network is up. See work item AC #4.
          setState({
            user: null,
            accessToken: preflight.accessToken,
            refreshToken: preflight.refreshToken,
            isAuthenticated: false,
            isLoading: false,
          });
          return;
        }
      }

      // As in Path A: /me rejected the token this leg is holding, and everything
      // below ENDS a session. Ask whose session is on the device before any of
      // it; nothing between here and the exchange below awaits.
      if (sessionReplaced()) return abandonToReplacementSession();

      // A rejected EMULATION access token is dead, and the stored refresh token
      // belongs to the sysadmin — exchanging it would resume their session
      // silently, behind a still-showing emulation banner (WI 59902ad6). End the
      // emulation explicitly instead; the provider reloads into sysadmin context.
      if (isEmulationAccessToken(storedAccessToken)) {
        endExpiredEmulation();
        setState({ ...initialState, isLoading: false });
        return;
      }

      // /me explicitly rejected — try refresh, unless the pre-flight already
      // rotated the pair, in which case this token IS fresh and the session is
      // genuinely over.
      if (storedRefreshToken && !preflight.rotated) {
        // As in Path A: a rotation that lands is adopted even if what follows
        // fails, so state never keeps serving a pair storage has replaced.
        let rotated: TokenPair | null = null;
        try {
          // Same single-flight join as Path A above — one exchange per rotating
          // refresh token, whichever transport gets there first.
          const tokens = await refreshAccessTokens();
          rotated = tokens;
          sessionToken = tokens.access_token;
          const currentUser = await authApi.getMe(tokens.access_token);
          if (sessionReplaced()) return abandonToReplacementSession();
          localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(currentUser));
          setState({
            user: currentUser,
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });
          return;
        } catch (refreshError) {
          if (!isAuthRejection(refreshError)) {
            if (sessionReplaced()) return abandonToReplacementSession();
            setState({
              user: null,
              accessToken: rotated?.access_token ?? storedAccessToken,
              refreshToken: rotated?.refresh_token ?? storedRefreshToken,
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }
        }
      }

      // Both tokens explicitly rejected on boot — but, as in the background
      // revalidation above, only if the keystore ever answered. With an
      // unanswered one we never presented a refresh token at all, so this is
      // the same "transient, keep the tokens for a later retry" state the /me
      // network-failure arm above lands in, not a rejected session to purge.
      if (!stored.answered) {
        setState({
          user: null,
          accessToken: storedAccessToken,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return;
      }

      // Last chance to ask before the two side effects, exactly as Path A's
      // `signOut` does: the refresh exchange above awaited, so the session may
      // have been replaced since the check ahead of it.
      if (sessionReplaced()) return abandonToReplacementSession();

      // purge the now-rejected user's persisted cache before clearing the session.
      void clearStoredAuth();
      void purgeQueryCache();
      setState({ ...initialState, isLoading: false });
    };

    if (bootstrapStarted.current) return;
    bootstrapStarted.current = true;
    loadStoredAuth();
  }, [purgeQueryCache]);

  // Listen for session expiry from axios interceptor and clear auth state immediately
  useEffect(() => {
    const handleSessionExpired = () => {
      // Not exposed to the staleness the bootstrap legs guard against, and not
      // by luck: `emitSessionExpired()` is dispatched SYNCHRONOUSLY from axios's
      // own `handleSessionExpired`, in the same tick as the
      // `localStorage.removeItem` calls just above it, so there is no await for
      // a different session to arrive in. This handler also closes over nothing
      // and reads nothing — it cannot be holding a token that storage has since
      // replaced. The staleness question for this path belongs one level up, to
      // the axios interceptor that decides to clear.
      //
      // A revoked/expired session is an auth transition too — purge the cache
      // so the previous user's data doesn't survive to the next login.
      void purgeQueryCache();
      setState({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
      });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [purgeQueryCache]);

  // Sync auth state across browser tabs when this tab becomes visible.
  // Uses visibilitychange (fires before focus) so we reload before React Query
  // refetches with a stale/missing token.
  useEffect(() => {
    const syncAuthOnVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // While the bootstrap is still running, stored auth and React state
      // disagree because nothing has DECIDED yet, not because another tab did
      // something. Reloading here throws the in-flight bootstrap away and
      // starts an identical one — and on the mobile shell that is a trap, not a
      // wasted round trip: the OTA updater commits a staged bundle by reloading
      // the WebView while the app is BACKGROUNDED (see shared/ota/confirmBundle.ts),
      // so the document that comes up is hidden and mid-bootstrap at the exact
      // moment the person returns and fires this handler. Each return reloads,
      // each reload restarts the bootstrap, and the app sits on
      // ProtectedRoute's spinner across relaunches — `launchMode="singleTask"`
      // resumes the same task, so reopening from the launcher does not clear it.
      if (state.isLoading) return;
      // While the app lock is up, stored auth and React state disagree BY
      // DESIGN — the token is in storage and the session has deliberately not
      // been hydrated. On mobile every return to the foreground fires
      // visibilitychange, so reloading here would come up locked and reload
      // again, forever.
      if (isAppLockEngaged()) return;
      const storedToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
      if (storedToken && !state.isAuthenticated) {
        // Another tab logged in — reload to pick up the session cleanly
        window.location.reload();
      } else if (!storedToken && state.isAuthenticated) {
        // Another tab logged out — reload to clear everything
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', syncAuthOnVisible);
    return () => {
      document.removeEventListener('visibilitychange', syncAuthOnVisible);
    };
  }, [state.isAuthenticated, state.isLoading]);

  // The one place a session is established from a server response that carries
  // both tokens and the user. Every caller goes through here.
  const writeSession = useCallback(async (response: AuthSessionResponse): Promise<void> => {
    // Purge any prior user's cached data (in-memory + persisted IndexedDB)
    // BEFORE establishing the new session, so the incoming user never renders
    // the previous user's lists from cache on a shared browser. Awaited so the
    // IndexedDB delete completes before we write the new token — otherwise
    // another tab, reloading on the new token via visibilitychange, could
    // restore the prior user's snapshot before removeClient() lands.
    await purgeQueryCache();
    // Write user and refresh token before access token — the storage event
    // listener in other tabs triggers on access_token and reads user/refresh from localStorage
    localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(response.user));
    // A keystore that will not take the write does NOT stop the sign-in. The
    // access token in localStorage is what authenticates; the refresh token is a
    // fallback credential that buys a longer session, and the server has already
    // issued both — so failing here would lock somebody out of an account that is
    // working, over a device fault. Reinstalling does not help them either: on iOS
    // the Keychain outlives the app it belonged to.
    //
    // Only a `SecureTokenStoreError` is degraded, which is the store's own way of
    // saying "the device could not answer" (including its deadline elapsing). Any
    // other error is a bug on this path and must still surface.
    let storedRefreshToken: string | null = response.refresh_token;
    try {
      await setRefreshToken(response.refresh_token);
    } catch (error) {
      if (!(error instanceof SecureTokenStoreError)) throw error;
      console.warn('[auth] Signed in without persisting the refresh token:', error);
      // Reported as absent rather than held, exactly as the bootstrap's hydrate
      // does: state that claims a token storage does not have would hand the
      // notifications WebSocket handshake a credential nothing can rotate.
      storedRefreshToken = null;
    }
    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, response.access_token);
    resetSessionExpiredGuard();

    setState({
      user: response.user,
      accessToken: response.access_token,
      refreshToken: storedRefreshToken,
      isAuthenticated: true,
      isLoading: false });
  }, [purgeQueryCache]);

  // Establish a session for a NEW identity (invitation acceptance). Adds the
  // cleanup a login doesn't need: a shared browser may still hold the previous
  // user's live emulation or demo session, neither of which belongs to the
  // incoming user. Cleared BEFORE the session write so no render sees the new
  // user wearing the old one's emulation.
  const establishSessionForNewIdentity = useCallback(
    async (response: AuthSessionResponse): Promise<void> => {
      clearEmulationData();
      clearDemoMode();
      await writeSession(response);
    },
    [writeSession],
  );

  // Establish a session from a completed-login response. Shared by password
  // login and the MFA challenge flow (the /mfa/verify response has this shape).
  const completeMfaLogin = useCallback(async (response: LoginResponse): Promise<string | null> => {
    await writeSession(response);
    // Return redirect URL if user should be redirected to their custom domain
    return response.redirect_to_domain || null;
  }, [writeSession]);

  const login = useCallback(async (credentials: LoginRequest): Promise<LoginOutcome> => {
    const browserTimezone = getBrowserTimezone();
    const result = await authApi.login({ ...credentials, timezone: browserTimezone });

    // MFA required: no JWT issued yet — hand the challenge back to the caller.
    if (isMfaChallenge(result)) {
      return { kind: 'mfa', challenge: result };
    }

    return { kind: 'authenticated', redirectUrl: await completeMfaLogin(result) };
  }, [completeMfaLogin]);

  const register = useCallback(async (data: RegisterRequest): Promise<RegisterResponse> => {
    // Registration now creates a pending user that needs email verification
    // No auto-login - user must verify email first
    const response = await authApi.register(data);
    return response;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (state.accessToken) {
        await authApi.logout(state.accessToken);
      }
    } catch {
      // Ignore logout errors
    }

    await clearStoredAuth();

    // Clear any active emulation session
    clearEmulationData();
    // Clear any active read-only demo session
    clearDemoMode();
    // Drop this user's cached data so the next user (or a logged-out shared
    // device) can't see it from the persisted query cache.
    await purgeQueryCache();

    setState({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false });
  }, [state.accessToken, purgeQueryCache]);

  const refreshAuth = useCallback(async () => {
    // Read from localStorage to get the latest token (may have been updated externally, e.g., by setup wizard)
    const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken) || state.accessToken;
    // During an active sysadmin emulation the stored refresh token still belongs
    // to the sysadmin — emulation only swaps the access token (see EmulationContext),
    // there is no emulation refresh token. Exchanging it here would mint a fresh
    // sysadmin access token and silently drop the operator out of emulation (the
    // avatar flips back to the sysadmin, the emulated tenant's data vanishes). This
    // is exactly what happened when saving certain tenant settings (e.g. default
    // language) called refreshAuth(). Instead, re-fetch the emulated user with the
    // existing emulation access token so callers still observe server-side changes,
    // while leaving the emulation session intact.
    if (accessToken && isEmulationAccessToken(accessToken)) {
      const user = await authApi.getMe(accessToken);
      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user));
      setState((prev) => ({
        ...prev,
        user,
        accessToken,
        isAuthenticated: true,
        isLoading: false,
      }));
      return;
    }

    // Storage only, deliberately: a background rotation writes localStorage and
    // NOT React state, so `state.refreshToken` is the one that can be spent —
    // and storage is what the shared exchange below will read anyway.
    if (!(await getRefreshToken().catch(() => null))) {
      throw new AuthApiError('NO_REFRESH_TOKEN', 'No refresh token available');
    }

    // Through the shared single-flight, like every other exchange in this file:
    // an explicit refreshAuth() (settings save, WebSocket EXPIRED_TOKEN) can
    // land on top of an in-flight axios refresh, and only one of them may spend
    // the rotating refresh token. It persists the rotated pair itself.
    const tokens = await refreshAccessTokens();
    const user = await authApi.getMe(tokens.access_token);

    localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user));

    setState({
      user,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      isAuthenticated: true,
      isLoading: false });
  }, [state.accessToken]);

  const updateUser = useCallback(async (data: Parameters<typeof authApi.updateMe>[1]) => {
    const accessToken = state.accessToken;
    if (!accessToken) {
      throw new AuthApiError('NO_ACCESS_TOKEN', 'No access token available');
    }

    try {
      const updatedUser = await authApi.updateMe(accessToken, data);

      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(updatedUser));

      setState((prev) => ({
        ...prev,
        user: updatedUser,
      }));

      return updatedUser;
    } catch (error) {
      // If 401 and we have a refresh token, try to refresh and retry
      // Backend returns AUTHENTICATION_REQUIRED for 401 errors
      const rejected =
        error instanceof AuthApiError &&
        (error.code === 'UNAUTHORIZED' || error.code === 'AUTHENTICATION_REQUIRED');

      // ...but NOT while emulating. Emulation swaps only the ACCESS token, so
      // the stored refresh token still belongs to the sysadmin: exchanging it
      // would mint a sysadmin access token, silently end the emulation, and then
      // replay this write AS THE SYSADMIN — saving the operator's own
      // preferred_language instead of the emulated user's. `refreshAuth` has
      // guarded this since the settings-save bug; this twin path never did.
      // An emulation access token is never refreshed; it just expires, and the
      // caller sees the 401 that says so.
      if (rejected && state.refreshToken && !isEmulationAccessToken(accessToken)) {
        // Shared single-flight again — this retry can race a concurrent axios
        // refresh. It persists the rotated pair itself.
        const tokens = await refreshAccessTokens();

        // Retry with new token
        const updatedUser = await authApi.updateMe(tokens.access_token, data);

        localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(updatedUser));

        setState((prev) => ({
          ...prev,
          user: updatedUser,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
        }));

        return updatedUser;
      }
      throw error;
    }
  }, [state.accessToken, state.refreshToken]);

  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      login,
      completeMfaLogin,
      establishSessionForNewIdentity,
      register,
      logout,
      refreshAuth,
      updateUser,
    }),
    [state, login, completeMfaLogin, establishSessionForNewIdentity, register, logout, refreshAuth, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * The signed-in user, or `null` — for a leaf that PERSONALIZES but does not require a
 * session.
 *
 * `useAuth` throws without a provider, and that is right for anything whose job depends
 * on the session: failing loudly beats rendering a signed-out surface as if it were
 * signed in. It is the wrong contract for a small shared control that merely wants to
 * know who is looking — a control mounted on many pages imposes that requirement on
 * every one of them, and on every test that renders any of them. The shared test render
 * (`test-utils/renderWithProviders`) supplies no auth deliberately, so a leaf that calls
 * `useAuth` takes down the whole page in ~1,800 test files at once, none of which are
 * about auth. `RemindMeButton` did exactly that when it was mounted beside `FollowButton`
 * on twelve detail pages — `FollowButton`, the control it sits next to, reads no auth at
 * all.
 *
 * So: use this where a null viewer degrades to something sensible (a personalization not
 * applied), and `useAuth` everywhere the session is the point.
 */
export function useAuthUser(): User | null {
  return useContext(AuthContext)?.user ?? null;
}

export { AuthApiError };
